// Pointing an api tool at a real endpoint.
//
// A tool with execution_type 'api' and an empty execution_config is the worst
// kind of broken: the LLM sees a clean parameter schema, fills it in
// confidently, announces that it has booked the appointment, and nothing was
// ever called. Nothing errors. The caller is simply told something untrue.
//
// The field set is not invented — it is exactly what the backend sends to the
// runtime as api_tools.tools_params (see AiAgentService.buildApiTools). Keeping
// the two in step matters more than the layout: a key spelled differently here
// is silently dropped there.
import React from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';

/** Every key the backend forwards, with the defaults it applies when absent. */
export interface ExecutionConfig {
  url?: string | null;
  method?: string | null;
  api_token?: string | null;
  headers?: Record<string, string> | null;
  param?: string;
  responseToLLM?: boolean;
  llmSuccessResponseMessage?: string;
  llmFailureResponseMessage?: string;
  fillerMessage?: string;
  requestTriggerWebSocket?: boolean;
  responseTriggerWebSocket?: boolean;
  pre_function_call_message?: string;
  /**
   * Follow-up tools (send_email / send_whatsapp) keep their choice here rather than
   * a URL — they are run by cpass after the call, not called over HTTP. Snake_case
   * because that is what the backend reads out of execution_config.
   */
  campaign_id?: string;
  template_id?: string;
}

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Tools cpass runs AFTER the call rather than during it.
 *
 * These are driven by the End_llm_task hook cpass installs on the agent when it
 * pushes, so there is no per-agent endpoint to fill in. Showing them the URL
 * form would invite someone to point one somewhere and end up with the action
 * running twice, or — if they pointed it at the wrong place — not at all.
 */
export const POST_CALL_TOOLS = new Set([
  'capture_lead', 'log_call', 'take_message',
  // Must match PostCallPipeline.SUPPORTED on the backend. If a tool cpass runs
  // after the call is missing here, this editor offers it the URL form — and a URL
  // on a post-call tool is never called, which is the precise failure the comment
  // at the top of this file describes.
  'send_email', 'send_whatsapp',
]);

/**
 * Post-call tools that DO need configuring: which follow-up to send.
 *
 * Unlike capture_lead, these have a real choice behind them, and with neither a
 * campaign nor a template the backend skips the send. Left blank the agent looks
 * configured and follows up with nothing.
 */
export const FOLLOW_UP_TOOLS = new Set(['send_email', 'send_whatsapp']);

export const isFollowUp = (name?: string | null): boolean => !!name && FOLLOW_UP_TOOLS.has(name);

export const isPostCall = (name?: string | null): boolean => !!name && POST_CALL_TOOLS.has(name);

/** An api tool with no url calls nothing, whatever else is filled in. */
export const isWired = (cfg?: ExecutionConfig | Record<string, any> | null): boolean =>
  !!(cfg && typeof cfg.url === 'string' && cfg.url.trim());

interface Props {
  value: ExecutionConfig;
  onChange: (next: ExecutionConfig) => void;
  /** Parameter names from the tool's JSON schema, offered as substitutions. */
  parameterNames: string[];
  inputCls: string;
  /** The tool's name, so a post-call tool can explain itself instead of asking for a URL. */
  toolName?: string;
  /** Standing campaigns this tool may enrol into, for the follow-up tools. */
  campaigns?: FollowUpOption[];
  /** Templates for a one-off send, for the follow-up tools. */
  templates?: FollowUpOption[];
}

export interface FollowUpOption { id: string; name: string; }

const ToolEndpointEditor: React.FC<Props> = ({ value, onChange, parameterNames, inputCls, toolName, campaigns, templates }) => {
  if (isFollowUp(toolName)) {
    return (
      <FollowUpEditor
        value={value}
        onChange={onChange}
        inputCls={inputCls}
        toolName={toolName as string}
        campaigns={campaigns || []}
        templates={templates || []}
      />
    );
  }
  if (isPostCall(toolName)) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900
                      dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
        <div className="font-medium">This one runs after the call, and needs no endpoint.</div>
        <p className="mt-1.5 opacity-90">
          When the conversation ends the voice runtime sends the transcript and the caller's
          number back to cpass, and cpass records the lead in your CRM from there. Nothing to
          configure here — connect a CRM under Integrations and it works.
        </p>
        <p className="mt-1.5 opacity-75">
          It is done afterwards on purpose: your CRM can take a couple of seconds to answer,
          and the caller should not be listening to silence while it does.
        </p>
      </div>
    );
  }
  return <InCallEditor value={value} onChange={onChange} parameterNames={parameterNames} inputCls={inputCls} />;
};

/**
 * Choose what the agent follows up with after the call.
 *
 * <p>Two ways, and they answer different needs. A STANDING CAMPAIGN is the one to
 * reach for: tracking, suppression, do-not-disturb and per-stage reporting all come
 * with it, and every caller lands in one place you can read. A TEMPLATE sends a
 * one-off with none of that — right when the message is a one-time courtesy and you
 * do not want a campaign per call filling the list with noise.
 *
 * <p>Neither selected is the state worth shouting about: the backend skips the send,
 * so the agent is configured to follow up and follows up with nothing.
 */
const FollowUpEditor: React.FC<{
  value: ExecutionConfig;
  onChange: (next: ExecutionConfig) => void;
  inputCls: string;
  toolName: string;
  campaigns: FollowUpOption[];
  templates: FollowUpOption[];
}> = ({ value, onChange, inputCls, toolName, campaigns, templates }) => {
  const cfg = value as Record<string, any>;
  const campaignId: string = cfg.campaign_id || '';
  const templateId: string = cfg.template_id || '';
  const channel = toolName === 'send_email' ? 'email' : 'WhatsApp';

  // Exactly one of the two. Setting a campaign clears the template rather than
  // leaving both: the backend prefers the campaign, so a leftover template would sit
  // in the config looking like it was going to be used.
  const pickCampaign = (id: string) => onChange({ ...cfg, campaign_id: id || undefined, template_id: undefined });
  const pickTemplate = (id: string) => onChange({ ...cfg, template_id: id || undefined, campaign_id: undefined });

  const label = 'text-xs text-gray-500 dark:text-gray-400';

  return (
    <div className="space-y-3">
      {!campaignId && !templateId && (
        <div className="flex gap-2 text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200
                        dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Nothing chosen, so nothing will be sent. The agent will show this tool as enabled and
            follow up with nothing — pick a campaign or a template below.
          </span>
        </div>
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900
                      dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
        <div className="font-medium">Sent after the call ends, not during it.</div>
        <p className="mt-1.5 opacity-90">
          {toolName === 'send_email'
            ? 'Goes to the email address on the caller\'s contact record. A voice call gives us their number, not their address — if we hold no email for them, this is skipped and the reason is recorded.'
            : 'Goes to the number that called. Do-not-disturb and recency rules are applied exactly as they are for a bulk campaign.'}
        </p>
      </div>

      <div>
        <label className={label}>Add them to a standing campaign</label>
        <select className={inputCls} value={campaignId} onChange={(e) => pickCampaign(e.target.value)}>
          <option value="">— none —</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
          Recommended. Tracking, suppression and reporting come with it, and every caller lands
          somewhere you can read.
        </p>
      </div>

      <div>
        <label className={label}>Or send a one-off {channel} from a template</label>
        <select className={inputCls} value={templateId} onChange={(e) => pickTemplate(e.target.value)}>
          <option value="">— none —</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
          No campaign, so no tracking or grouped reporting — right for a one-time courtesy.
        </p>
      </div>

      {campaigns.length === 0 && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          No standing campaigns yet. Create one under {channel === 'email' ? 'Email' : 'WhatsApp'} → Campaigns
          and tick “Keep this campaign open”.
        </p>
      )}
    </div>
  );
};

const InCallEditor: React.FC<Omit<Props, 'toolName' | 'campaigns' | 'templates'>> = ({ value, onChange, parameterNames, inputCls }) => {
  const set = (patch: Partial<ExecutionConfig>) => onChange({ ...value, ...patch });

  // Headers are edited as pairs but stored as an object, which is what the
  // backend forwards. Kept as an array in flight so a half-typed key does not
  // vanish the row it belongs to.
  const headerPairs = Object.entries(value.headers || {});
  const setHeader = (i: number, k: string, v: string) => {
    const next = headerPairs.map(([hk, hv], idx) => (idx === i ? [k, v] : [hk, hv]));
    set({ headers: Object.fromEntries(next.filter(([hk]) => hk !== '')) as Record<string, string> });
  };
  const addHeader = () => set({ headers: { ...(value.headers || {}), '': '' } });
  const removeHeader = (i: number) =>
    set({ headers: Object.fromEntries(headerPairs.filter((_, idx) => idx !== i)) as Record<string, string> });

  const label = 'text-xs text-gray-500 dark:text-gray-400';

  return (
    <div className="space-y-3">
      {!isWired(value) && (
        <div className="flex gap-2 text-xs rounded-lg px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200
                        dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            This tool has no endpoint. The agent will still offer it on a call and report success —
            nothing will be called. Give it a URL, or change its type so it stops pretending.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className={label + ' md:col-span-3'}>
          Endpoint URL
          <input
            className={inputCls + ' font-mono text-xs'}
            value={value.url || ''}
            onChange={e => set({ url: e.target.value })}
            placeholder="https://api.yourbusiness.com/appointments"
          />
        </label>
        <label className={label}>
          Method
          <select className={inputCls} value={value.method || 'POST'} onChange={e => set({ method: e.target.value })}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
      </div>

      <label className={label + ' block'}>
        Bearer token <span className="text-gray-400">— optional</span>
        <input
          type="password"
          className={inputCls + ' font-mono text-xs'}
          value={value.api_token || ''}
          onChange={e => set({ api_token: e.target.value })}
          placeholder="sent as the request's auth token"
        />
      </label>

      <div>
        <div className="flex items-center justify-between">
          <span className={label}>Extra headers</span>
          <button type="button" onClick={addHeader}
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {headerPairs.length === 0 ? (
          <p className="text-[11px] text-gray-400 mt-1">None.</p>
        ) : (
          <div className="space-y-2 mt-1">
            {headerPairs.map(([k, v], i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className={inputCls + ' font-mono text-xs'} value={k} placeholder="Header"
                       onChange={e => setHeader(i, e.target.value, v)} />
                <input className={inputCls + ' font-mono text-xs'} value={v} placeholder="value"
                       onChange={e => setHeader(i, k, e.target.value)} />
                <button type="button" onClick={() => removeHeader(i)}
                        className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <label className={label + ' block'}>
        Request body
        <textarea
          rows={4}
          className={inputCls + ' font-mono text-xs'}
          value={value.param || ''}
          onChange={e => set({ param: e.target.value })}
          placeholder={'{"service": "%(service)s", "when": "%(preferred_time)s"}'}
        />
        <span className="block text-[11px] text-gray-400 mt-1">
          Forwarded to the runtime as <code>param</code>.
          {parameterNames.length > 0 && <> This tool's parameters: {parameterNames.map(p => <code key={p} className="mx-1">{p}</code>)}</>}
        </span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className={label}>
          Said before the call is made
          <input className={inputCls} value={value.fillerMessage || ''}
                 onChange={e => set({ fillerMessage: e.target.value })}
                 placeholder="Let me check that for you…" />
          <span className="block text-[11px] text-gray-400 mt-1">
            Covers the silence while your endpoint answers.
          </span>
        </label>
        <label className={label}>
          Said before the function runs
          <input className={inputCls} value={value.pre_function_call_message || ''}
                 onChange={e => set({ pre_function_call_message: e.target.value })} />
        </label>
        <label className={label}>
          On success
          <input className={inputCls} value={value.llmSuccessResponseMessage || ''}
                 onChange={e => set({ llmSuccessResponseMessage: e.target.value })}
                 placeholder="That's booked." />
        </label>
        <label className={label}>
          On failure
          <input className={inputCls} value={value.llmFailureResponseMessage || ''}
                 onChange={e => set({ llmFailureResponseMessage: e.target.value })}
                 placeholder="I couldn't complete that just now." />
        </label>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={value.responseToLLM !== false}
                 onChange={e => set({ responseToLLM: e.target.checked })} />
          Give the response back to the agent
          <span className="text-gray-400">— off means it cannot tell the caller what your endpoint returned</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={!!value.requestTriggerWebSocket}
                 onChange={e => set({ requestTriggerWebSocket: e.target.checked })} />
          Notify over websocket on request
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={!!value.responseTriggerWebSocket}
                 onChange={e => set({ responseTriggerWebSocket: e.target.checked })} />
          Notify over websocket on response
        </label>
      </div>
    </div>
  );
};

export default ToolEndpointEditor;
